/**
 * Evaluation process: samples, sessions, gates and points.
 *
 * The one contract behind the backend checks, the Zod enums and the frontend
 * render gates for the gated evaluation flow. Design and decisions D1-D8:
 * docs/specs/2026-10-13-evaluation-process-design.md.
 *
 * A product moves through three stages, each closed by a gate its PM releases:
 *
 *   1 CNC sample ── Gate A ── 2 Back-and-forth ── Gate B ── 3 PLS sample ── Gate C ── complete
 *
 * Gates are per product (D2): every sample of the product moves together. The
 * current stage is DERIVED from the released gates (see `currentStage` in
 * evaluation-process.compute.ts) and is never stored.
 *
 * Pure logic over these types lives in evaluation-process.compute.ts, following
 * the physical-asset.types / physical-asset.compute split. UI copy lives in the
 * frontend's copy.ts for the prototype (D7) and moves to en locale keys before
 * any merge. The exception is the stage and slot NAMES, which are here because
 * the server writes them into a sample's permanent history (the
 * PHYSICAL_ASSET_SAMPLE_TYPE_LABELS precedent); copy.ts reads them from here.
 */
// ─── Stages and gates ───────────────────────────────────────────────────────
/** The three stages, in order. Shown as "1 CNC sample", "2 Back-and-forth", "3 PLS sample". */
export const EVAL_PROCESS_STAGES = ['cnc', 'iteration', 'pls'];
/**
 * Stage names without their number ("CNC sample"); `evalStageLabel` adds it.
 * Here, not in the frontend copy, because the server writes them into a
 * sample's history, which is never rewritten.
 */
export const EVAL_PROCESS_STAGE_NAMES = {
    cnc: 'CNC sample',
    iteration: 'Back-and-forth',
    pls: 'PLS sample',
};
/** Where the product is once gate C has been released. Not a stage: nothing opens. */
export const EVAL_PROCESS_COMPLETE = 'complete';
/** The gates, in the order they must be released. */
export const EVAL_GATES = ['A', 'B', 'C'];
/** The gate that closes each stage. */
export const GATE_AFTER_STAGE = {
    cnc: 'A',
    iteration: 'B',
    pls: 'C',
};
/** The stage each gate closes: the inverse of {@link GATE_AFTER_STAGE}. */
export const STAGE_BEFORE_GATE = {
    A: 'cnc',
    B: 'iteration',
    C: 'pls',
};
/**
 * The sample type each stage evaluates (D16): CNC samples at 1 CNC sample and
 * 2 Back-and-forth (a revised engineering sample is labelled CNC), PLS samples
 * at 3 PLS sample. Either site, EU or US, at any stage: the two sites work
 * towards the same gate (D2).
 */
export const EVAL_STAGE_SAMPLE_TYPE = {
    cnc: 'cnc',
    iteration: 'cnc',
    pls: 'pls',
};
// ─── Slots and the forms behind them ────────────────────────────────────────
/**
 * Every evaluation the process knows, in display order. A slot is a role in
 * the process, not a form: several slots open the same form with a different
 * set of sections (D4). Not every stage lists every slot: see
 * {@link EVAL_STAGE_SLOTS}.
 */
export const EVAL_PROCESS_SLOTS = [
    'supplier_spec',
    'engineer',
    'hardware_check',
    'photos',
    'software_base',
    'photometrics',
    'dim_curve',
    'pm_spec_review',
    'user_experience',
];
/**
 * The slots each stage lists, in display order (2026-10-13): Photos belongs to
 * the two sample stages, CNC and PLS, and not to Back-and-forth. Only a stage's
 * own slots are shown, take settings, files or a completion, and count toward
 * the gate that closes it.
 */
export const EVAL_STAGE_SLOTS = {
    cnc: EVAL_PROCESS_SLOTS,
    // Back-and-forth (D33, revised D37 and D38 2026-10-14) works through the
    // points carried over from the CNC sample and the software versions: each
    // version a review of the Open software points (D38; the full Software Base
    // Eval runs at CNC and PLS only), with an optional Dim Curve of its own. No
    // photos, no factory spec (D27), no User Experience (its stage-1 points are
    // among the carried-over ones), and Engineer Testing, Hardware Check and
    // Photometrics are not done again: any data of theirs at this stage is
    // kept, just not shown or counted.
    iteration: ['software_base', 'dim_curve'],
    pls: EVAL_PROCESS_SLOTS,
};
// ─── Back-and-forth: software versions (D33) ────────────────────────────────
/** The stage whose software evaluations are tracked per software version (D33). */
export const EVAL_SOFTWARE_VERSION_STAGE = 'iteration';
/**
 * The slot that stands for the software versions at that stage: its row is
 * the versions, and gate B reads it through the latest version's review
 * (D38). No form round of it opens there; rounds opened before D38 are kept
 * and carried over as versions.
 */
export const EVAL_SOFTWARE_VERSION_SLOT = 'software_base';
/** Upload slots kept per software version at that stage: each version has its own Dim Curve report. */
export const EVAL_PER_VERSION_SLOTS = ['dim_curve'];
/**
 * Slots a stage lists that its gate does not wait on (D37): Dim Curve at
 * Back-and-forth, run on a software version only when that version needs one
 * (the product lead: "you will not always need to run a dim curve test for
 * all softwares"); it is done properly again at PLS.
 */
export const EVAL_STAGE_OPTIONAL_SLOTS = {
    iteration: ['dim_curve'],
};
/** The software form's Firmware Version answer (its identity section, fixture-info): prefilled with the version added. */
export const EVAL_FIRMWARE_VERSION_FIELD_PATH = 'firmware-version';
/** The longest version string ("V1.0.9"). */
export const EVAL_SOFTWARE_VERSION_MAX = 40;
/**
 * A software version's key (`versionKey`): SWV-000001 for a version added as
 * a review (D38), or the evaluation id of a Software Base Eval round added
 * before D38 and carried over. Checked against the product's versions on
 * every write; the pattern only keeps it safe as a stored field name.
 */
export const EVAL_SOFTWARE_VERSION_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * Where a full software evaluation listed under a version came from: a
 * Software Base Eval round the process opened at Back-and-forth (before D38),
 * or one started from Gooch's Software Evaluations card ("New Eval") during
 * the stage, listed automatically and read-only.
 */
export const EVAL_SOFTWARE_VERSION_ORIGINS = ['process', 'software_evaluations'];
/**
 * What a software version is (D38): a review added with "Add software
 * version", or a Software Base Eval round added at Back-and-forth before D38,
 * carried over as a review (its evaluation kept, untouched, as the version's
 * full software evaluation).
 */
export const EVAL_SOFTWARE_VERSION_KINDS = ['review', 'process_round'];
/** SWV-000001: a software version added as a review (D38). */
export const EVAL_SOFTWARE_REVIEW_KEY_PREFIX = 'SWV';
/**
 * A point's outcome on a software version (D38): raised in that version's
 * review ("Add point"), checked and still open, or fixed (which closes it,
 * "Checked on V1.0.9").
 */
export const EVAL_SOFTWARE_CHECK_OUTCOMES = ['raised', 'still_open', 'fixed'];
/** The outcomes a reviewer records on a listed point; 'raised' is recorded by the review's Add point. */
export const EVAL_SOFTWARE_REVIEW_OUTCOMES = ['still_open', 'fixed'];
/** The longest "Dim curve not needed" note. */
export const EVAL_DIM_CURVE_NOTE_MAX = 500;
/**
 * Slot names, for the same reason as {@link EVAL_PROCESS_STAGE_NAMES}. The ids
 * never change; only these words do (renamed 2026-10-13 at the product
 * lead's request). Asset history written before then keeps the old words.
 */
export const EVAL_PROCESS_SLOT_LABELS = {
    supplier_spec: 'Supplier Spec',
    engineer: 'Engineer Testing',
    hardware_check: 'Hardware Check',
    photos: 'Photos',
    software_base: 'Software Base Eval',
    photometrics: 'Photometrics',
    dim_curve: 'Dim Curve Automated',
    pm_spec_review: 'PM Spec Review',
    user_experience: 'User Experience / General Eval',
};
/**
 * How a slot is worked (D4, revised 2026-10-13):
 * - `form`: opens an evaluation form ({@link EVAL_SLOT_FORMS}); done at a stage
 *   once one of its evaluations there is submitted, locked or published.
 * - `upload_complete`: no form. Files are uploaded against the product, stage
 *   and slot ({@link EVAL_SLOT_UPLOADS}), then someone presses Completed; done
 *   at a stage once Completed. Photos, Photometrics, Dim Curve Automated.
 * - `points_complete`: no form. Findings are raised as points under the
 *   headings of the PM Product Evaluation ({@link EVAL_UX_HEADINGS}), then
 *   someone presses Completed (zero points is allowed: nothing to raise); done
 *   at a stage once Completed. User Experience / General Eval.
 * - `review_complete`: no form. The PM cross-checks what the factory declared
 *   against what was measured and confirms or flags each item, then presses
 *   Completed; done at a stage once Completed. PM Spec Review (D28).
 */
export const EVAL_SLOT_KINDS = ['form', 'upload_complete', 'points_complete', 'review_complete'];
export const EVAL_SLOT_KIND = {
    // The factory's Supplier Spec: uploaded (a .docx is read), reviewed, Completed (D27).
    supplier_spec: 'upload_complete',
    engineer: 'form',
    hardware_check: 'form',
    photos: 'upload_complete',
    software_base: 'form',
    photometrics: 'upload_complete',
    dim_curve: 'upload_complete',
    pm_spec_review: 'review_complete',
    user_experience: 'points_complete',
};
/** Hardware 'standard' form: template `eval-template-standard`, formId `standard`. */
const STANDARD_FORM = {
    evaluationType: 'hardware',
    templateProductId: 'eval-template-standard',
    formId: 'standard',
    identitySectionId: 'development_sample',
};
/** Software form: template `eval-template-software`, formId `software`. */
const SOFTWARE_FORM = {
    evaluationType: 'software',
    templateProductId: 'eval-template-software',
    formId: 'software',
    identitySectionId: 'fixture-info',
};
/**
 * Slot → form (D4). null for a slot that opens no form: the `upload_complete`
 * slots, worked by uploading files and pressing Completed
 * ({@link EVAL_SLOT_UPLOADS}), and the `points_complete` slot, worked by
 * raising points under headings and pressing Completed ({@link EVAL_UX_HEADINGS}).
 *
 * Section ids come from the seed (packages/backend/seeds/evalQuestions.json).
 * `wws` is the WWS section; `interior_wws` is a different one.
 */
export const EVAL_SLOT_FORMS = {
    supplier_spec: null,
    engineer: { ...STANDARD_FORM, sectionIds: null, focusSectionId: null },
    hardware_check: { ...STANDARD_FORM, sectionIds: ['development_sample', 'wws'], focusSectionId: null },
    photos: null,
    software_base: { ...SOFTWARE_FORM, sectionIds: null, focusSectionId: null },
    photometrics: null,
    dim_curve: null,
    pm_spec_review: null,
    user_experience: null,
};
// ─── Upload + Completed slots ───────────────────────────────────────────────
/**
 * Where a slot's files came from. Only 'upload' is written today. 'integration'
 * is reserved for Dim Curve Automated reading straight from Michaël's automated
 * dim-curve app (design doc, "Dim Curve integration point"): those files and
 * that completion would carry this source instead of a person's upload.
 */
/**
 * Where a slot file came from: uploaded here, written by an integration
 * (reserved for Dim Curve Automated), or `reference`: a file already in the
 * product's OneDrive / SharePoint folder, added by reference (D32; never
 * copied, never deleted by ATLAS).
 */
export const EVAL_SLOT_FILE_SOURCES = ['upload', 'integration', 'reference'];
/** One file, single PUT: the evaluation-asset ceiling (Cloudflare caps a request at 100 MB). */
export const EVAL_SLOT_FILE_MAX_BYTES = 50 * 1024 * 1024;
/** One photo: ATLAS's image ceiling elsewhere (help, bulletin, fixture and competitor images). */
export const EVAL_SLOT_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
/** Photo extensions: JPEG, PNG, WebP, and HEIC/HEIF from phones (kept; most browsers cannot show them). */
export const EVAL_SLOT_PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
/** HEIC/HEIF are stored and downloadable but most browsers cannot draw them: shown as a file tile. */
export const EVAL_SLOT_UNPREVIEWABLE_PHOTO_EXTENSIONS = ['.heic', '.heif'];
/** Slot → what it accepts, for the `upload_complete` slots; null for every other slot. */
export const EVAL_SLOT_UPLOADS = {
    // The factory's Supplier Specs: the Word template is read; a PDF or workbook is kept, not read (D27).
    supplier_spec: { accept: ['.docx', '.pdf', '.xlsx'], maxBytes: EVAL_SLOT_FILE_MAX_BYTES, maxFiles: 25, images: false },
    engineer: null,
    hardware_check: null,
    photos: { accept: EVAL_SLOT_PHOTO_EXTENSIONS, maxBytes: EVAL_SLOT_PHOTO_MAX_BYTES, maxFiles: 200, images: true },
    software_base: null,
    // Viso outputs: the report, the .fixture project, IES and EULUMDAT photometry, data sheets.
    photometrics: {
        accept: ['.pdf', '.fixture', '.ies', '.ldt', '.xlsx', '.csv', '.zip'],
        maxBytes: EVAL_SLOT_FILE_MAX_BYTES,
        maxFiles: 25,
        images: false,
    },
    // The dim-curve report.
    dim_curve: { accept: ['.pdf', '.xlsx', '.csv', '.zip'], maxBytes: EVAL_SLOT_FILE_MAX_BYTES, maxFiles: 25, images: false },
    pm_spec_review: null,
    user_experience: null,
};
/**
 * The content type a slot file is stored and signed with, by extension. The
 * server picks it, never the browser: a browser reports '' for .ies, .ldt and
 * .fixture, and the presigned PUT binds the type into its signature.
 */
export const EVAL_SLOT_FILE_CONTENT_TYPES = {
    '.pdf': 'application/pdf',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
    '.zip': 'application/zip',
    '.ies': 'application/octet-stream',
    '.ldt': 'application/octet-stream',
    '.fixture': 'application/octet-stream',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
};
export const EVAL_SLOT_FILENAME_MAX = 255;
/** EVF-000001: the counter pads to six digits and grows past them. The routes accept only this shape. */
export const EVAL_SLOT_FILE_ID_PATTERN = /^EVF-\d{6,}$/;
/** pending until the browser's PUT is verified in storage; failed when verification failed. */
export const EVAL_SLOT_FILE_STATUSES = ['pending', 'verified', 'failed'];
export const EVAL_SLOT_COMPLETION_ACTIONS = ['completed', 'reopened'];
// ─── User Experience / General Eval: headings and points ─────────────────────
/**
 * The seven headings of the Storm 1 "PM Product Evaluation" form, in order.
 * The User Experience / General Eval slot is worked by raising points under
 * them (`EvaluationPoint.heading`), not by a form.
 */
export const EVAL_UX_HEADINGS = [
    'exterior',
    'interior',
    'atmospheric',
    'performance',
    'video',
    'networking',
    'final_comments',
];
/** The legend I–VII of the PM Product Evaluation. */
export const EVAL_UX_HEADING_INFO = {
    exterior: { numeral: 'I', title: 'Fixture Exterior Features/Functions', scope: 'all', canBeNotApplicable: false },
    interior: { numeral: 'II', title: 'Fixture Interior Features/Functions', scope: 'all', canBeNotApplicable: false },
    atmospheric: {
        numeral: 'III',
        title: 'Atmospheric Fixture Features/Functions',
        scope: 'atmospheric',
        canBeNotApplicable: true,
    },
    performance: { numeral: 'IV', title: 'Fixture Performance Features/Functions', scope: 'all', canBeNotApplicable: false },
    video: { numeral: 'V', title: 'Fixture Video-Specific Features/Functions', scope: 'video', canBeNotApplicable: true },
    networking: {
        numeral: 'VI',
        title: 'Fixture Networking Features/Functions',
        scope: 'networking',
        canBeNotApplicable: true,
    },
    final_comments: { numeral: 'VII', title: 'Final Comments', scope: 'all', canBeNotApplicable: false },
};
/** The slot whose points carry a heading. */
export const EVAL_UX_SLOT = 'user_experience';
/** Submission statuses that count a slot as done at a stage (D3). */
export const EVAL_SLOT_DONE_STATUSES = ['submitted', 'locked', 'published'];
// ─── Prefill of a slot's new submission ─────────────────────────────────────
/** Field path of the hardware form's "Stage" select (development_sample section). */
export const EVAL_SAMPLE_STAGE_FIELD_PATH = 'developmentSampleStage';
/**
 * Field path of the hardware form's "Engineer" person select. It stores the
 * person's display NAME, not a USR- id (PersonSelectField), so the prefill does too.
 */
export const EVAL_ENGINEER_FIELD_PATH = 'Engineer';
/**
 * The `developmentSampleStage` answer for each stage, most wanted first, checked
 * against the live select's options. The live options (standard and
 * user_experience, 2026-10-13) are 1st, 2nd, 3rd, final, pls, other; the
 * software form has no stage select at all.
 *
 * - CNC sample is the first sample: '1st'.
 * - Back-and-forth is the next sample, '2nd'; a form without it gets the
 *   closest it has ('3rd', then 'other').
 * - PLS sample is 'pls' ('final' only where a form has no 'pls').
 */
export const STAGE_SAMPLE_ANSWER_PREFERENCES = {
    cnc: ['1st'],
    iteration: ['2nd', '3rd', 'other'],
    pls: ['pls', 'final'],
};
/** The answer each stage prefills when a form's options are unknown: the first preference. */
export const STAGE_TO_SAMPLE_STAGE_ANSWER = {
    cnc: STAGE_SAMPLE_ANSWER_PREFERENCES.cnc[0],
    iteration: STAGE_SAMPLE_ANSWER_PREFERENCES.iteration[0],
    pls: STAGE_SAMPLE_ANSWER_PREFERENCES.pls[0],
};
// ─── Points ─────────────────────────────────────────────────────────────────
/**
 * Legacy: the four priorities points carried before the product lead's
 * Open / Closed / Future change (2026-10-13). Rows written with one keep it
 * (data retention), and a create may still send one (an import, say), but
 * nothing shows, requires, sorts or gates on it any more.
 */
export const EVAL_POINT_PRIORITIES = ['blocker', 'must_fix', 'should_fix', 'nice_to_have'];
/**
 * A point's state, as stored. Shown as Open, Closed and Future
 * ({@link EVAL_POINT_STATUS_LABELS}); the stored names predate the change and
 * are kept, so no row is rewritten.
 */
export const EVAL_POINT_STATUSES = ['open', 'resolved', 'parked'];
/** What each state is called everywhere a person reads it. */
export const EVAL_POINT_STATUS_LABELS = {
    open: 'Open',
    resolved: 'Closed',
    parked: 'Future',
};
/** The gates that need every point Closed or Future (gate A lets points carry over to the revised sample). */
export const EVAL_GATES_NEEDING_NO_OPEN_POINTS = ['B', 'C'];
/** Where a point came from: a person, or a Fail answer in a process form (raised by the server). */
export const EVAL_POINT_ORIGINS = ['manual', 'fail_answer'];
export const EVAL_POINT_TITLE_MAX = 200;
export const EVAL_POINT_DETAIL_MAX = 4000;
/** A status change's note (how it was verified, why it waits) or the reason for unticking Safety. */
export const EVAL_POINT_NOTE_MAX = 2000;
/** EVP-000001: the counter pads to six digits and grows past them. The routes accept only this shape. */
export const EVAL_POINT_ID_PATTERN = /^EVP-\d{6,}$/;
/** EPF-000001: a point's photo. */
export const EVAL_POINT_FILE_ID_PATTERN = /^EPF-\d{6,}$/;
/** A point's photos: the Photos slot's types and size, at most this many live on one point. */
export const EVAL_POINT_PHOTO_MAX_FILES = 20;
/** Who a release was made as (D1). */
export const EVAL_GATE_RELEASED_AS = ['pm', 'admin'];
export const EVAL_GATE_NOTE_MAX = 2000;
// ─── Sessions ───────────────────────────────────────────────────────────────
export const EVAL_SESSION_FINISH_REASONS = ['finished', 'idle', 'superseded'];
/** The reasons a client may finish with. 'superseded' is only ever set by the server. */
export const EVAL_SESSION_CLIENT_FINISH_REASONS = ['finished', 'idle'];
/** Auto-finish after this long with no activity (D6). */
export const EVAL_SESSION_IDLE_MS = 15 * 60 * 1000;
/** The browser sends a heartbeat at most this often, and only after activity (D6). */
export const EVAL_SESSION_HEARTBEAT_MS = 60 * 1000;
/** Most sessions one GET /sessions returns. */
export const EVAL_SESSION_LIST_MAX = 100;
/**
 * EVS-000001: the counter pads to six digits and grows past them. The routes
 * accept only this shape, and the submission page reads `?session=` with it.
 */
export const EVAL_SESSION_ID_PATTERN = /^EVS-\d{6,}$/;
/** Section ids the section-site routes accept (definition section ids). */
export const EVAL_SECTION_ID_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;
// ─── Error codes ────────────────────────────────────────────────────────────
/**
 * `error.code` (or `error.details.reason` on a 400/409, whose classes fix the
 * code) the evaluation-process routes answer with, so the client can match on
 * a name instead of a message.
 */
export const EVAL_PROCESS_ERROR_CODES = {
    /** 403: the product has no active PM, so only an admin can act. */
    gateNoPm: 'EVAL_GATE_NO_PM',
    /** 403: the caller is not the product's PM (and not an admin). */
    gatePmRequired: 'EVAL_GATE_PM_REQUIRED',
    /** 409: that gate is not the next one. */
    gateOutOfOrder: 'EVAL_GATE_OUT_OF_ORDER',
    /** 409: the stage is not ready and no override was confirmed. */
    gateNotReady: 'EVAL_GATE_NOT_READY',
    /** 400: an override needs a non-empty note. */
    gateNoteRequired: 'EVAL_GATE_NOTE_REQUIRED',
    /** 409: gate C is released; no stage is open. */
    processComplete: 'EVAL_PROCESS_COMPLETE',
    /** 400: the sample belongs to another product. */
    sessionAssetProduct: 'EVAL_SESSION_ASSET_PRODUCT',
    /** 400: the sample is deleted, retired or lost. */
    sessionAssetUnavailable: 'EVAL_SESSION_ASSET_UNAVAILABLE',
    /** 409: the session has already finished. */
    sessionClosed: 'EVAL_SESSION_CLOSED',
    /** 400: the slot has no form yet. */
    slotNoForm: 'EVAL_SLOT_NO_FORM',
    /** 400: a point's linked sample, session or evaluation is not one of this product's. */
    pointLinkInvalid: 'EVAL_POINT_LINK_INVALID',
    /** 400: files are for the upload slots (Photos, Photometrics, Dim Curve Automated) only. */
    slotNotUpload: 'EVAL_SLOT_NOT_UPLOAD',
    /** 400: Completed and Reopen are for the upload slots and User Experience / General Eval, not the form slots. */
    slotNotCompletable: 'EVAL_SLOT_NOT_COMPLETABLE',
    /** 400: that stage does not list that slot (Photos at Back-and-forth). */
    slotNotInStage: 'EVAL_SLOT_NOT_IN_STAGE',
    /** 400: only the product-type headings (III, V, VI) can be Not applicable. */
    uxHeadingRequired: 'EVAL_UX_HEADING_REQUIRED',
    /** 400: a heading belongs only on a User Experience / General Eval point. */
    pointHeadingInvalid: 'EVAL_POINT_HEADING_INVALID',
    /** 409: that stage is not open for this change (a past stage, or for files and Completed any stage but the current one). */
    slotStageClosed: 'EVAL_SLOT_STAGE_CLOSED',
    /** 409: an upload slot's Completed needs at least one uploaded file. */
    slotNoFiles: 'EVAL_SLOT_NO_FILES',
    /** 409: the slot is Completed at that stage; reopen it to change its files or headings. */
    slotCompleted: 'EVAL_SLOT_COMPLETED',
    /** 400: the file type is not one the slot accepts. */
    slotFileType: 'EVAL_SLOT_FILE_TYPE',
    /** 409: the slot already holds the most files it may. */
    slotFileLimit: 'EVAL_SLOT_FILE_LIMIT',
    /** 400: the upload did not arrive in storage, or not at the declared size. */
    slotFileUpload: 'EVAL_SLOT_FILE_UPLOAD',
    /** 400: the assignee is not an active internal user who can work an evaluation. */
    slotAssigneeInvalid: 'EVAL_SLOT_ASSIGNEE_INVALID',
    /** 400: the session named is not the caller's own open session on this product. */
    slotSessionInvalid: 'EVAL_SLOT_SESSION_INVALID',
    /** 409: the sample's type does not fit the stage (a CNC sample at 3 PLS sample, a PLS sample before it; D16). */
    sampleStageMismatch: 'EVAL_SAMPLE_STAGE_MISMATCH',
    /** 409: the sample has no sample type yet, so it cannot be evaluated until it is labelled CNC or PLS (D16). */
    sampleUnlabelled: 'EVAL_SAMPLE_UNLABELLED',
    /** 409: a Safety point cannot go to Future; it has to be Closed. */
    pointSafetyFuture: 'EVAL_POINT_SAFETY_FUTURE',
    /** 400: unticking Safety needs a reason. */
    pointNoteRequired: 'EVAL_POINT_NOTE_REQUIRED',
    /** 409: every evaluation of that stage × slot is done; ask for a new round to evaluate it again. */
    slotRoundDone: 'EVAL_SLOT_ROUND_DONE',
    /** 400: no such section in that evaluation's form. */
    sectionUnknown: 'EVAL_SECTION_UNKNOWN',
    /** 409: the Supplier Spec's newest reading has not been reviewed yet. */
    supplierSpecNotReviewed: 'EVAL_SUPPLIER_SPEC_NOT_REVIEWED',
    /** 400: the uploaded Word document is not a Supplier Spec the reader knows. */
    supplierSpecUnreadable: 'EVAL_SUPPLIER_SPEC_UNREADABLE',
    /** 409: PM Spec Review has items with both figures still to confirm or flag, or open flags. */
    specReviewIncomplete: 'EVAL_SPEC_REVIEW_INCOMPLETE',
    /** 409: that review item or flag was settled a moment ago (raised, confirmed, dismissed or withdrawn). */
    specAlreadySettled: 'EVAL_SPEC_ALREADY_SETTLED',
    /** 400: no such item in this stage's review, or no such measured question. */
    specItemUnknown: 'EVAL_SPEC_ITEM_UNKNOWN',
    /** 409: a point that follows a Fail answer cannot be deleted; close it, or change the answer. */
    pointFollowsAnswer: 'EVAL_POINT_FOLLOWS_ANSWER',
    /** 409: an Open Safety point holds gate B or C, and no override releases past it. */
    gateSafetyOpen: 'EVAL_GATE_SAFETY_OPEN',
    /** 409: someone else is live in that section (D30); `details` name them. Also a change to a field there. */
    sectionLocked: 'EVAL_SECTION_LOCKED',
    /** 409: the caller no longer holds that section (released by the PM or an admin, or taken over once stale). */
    sectionLockLost: 'EVAL_SECTION_LOCK_LOST',
    /** 400: a new software version needs its version string, and Dim Curve at Back-and-forth the version it is for (D33, D38). */
    softwareVersionRequired: 'EVAL_SOFTWARE_VERSION_REQUIRED',
    /** 400: not a software version of this product at Back-and-forth, or a version given where the slot keeps none (D33). */
    softwareVersionInvalid: 'EVAL_SOFTWARE_VERSION_INVALID',
    /** 409: that software version is already listed (D33). */
    softwareVersionExists: 'EVAL_SOFTWARE_VERSION_EXISTS',
    /** 409: the point is Closed or Future, so no phone link is made for its photos, nor an outcome recorded on a version (reopen it first). */
    pointNotOpen: 'EVAL_POINT_NOT_OPEN',
    /** 400 (DNA): the file is not a .DNA the reader knows (not JSON, a spreadsheet, or no DNA sections); the form stays full. */
    dnaUnreadable: 'EVAL_DNA_UNREADABLE',
    /** 400 (DNA): only a process Software Base Eval at CNC or PLS takes a DNA. */
    dnaNotApplicable: 'EVAL_DNA_NOT_APPLICABLE',
    /** 409 (DNA): the evaluation is no longer a draft, so its DNA stays as it is. */
    dnaEvaluationClosed: 'EVAL_DNA_EVALUATION_CLOSED',
    /** 400 (DNA): not one of this product's DNA versions. */
    dnaVersionUnknown: 'EVAL_DNA_VERSION_UNKNOWN',
    /** 400 (D38): at Back-and-forth software is reviewed per version; the Software Base Eval form runs at CNC and PLS. */
    softwareReviewOnly: 'EVAL_SOFTWARE_REVIEW_ONLY',
    /** 400 (D38): that point is not on that version's review. */
    softwarePointNotListed: 'EVAL_SOFTWARE_POINT_NOT_LISTED',
};
//# sourceMappingURL=evaluation-process.types.js.map